import type {
  ClaimCreateRequest,
  ClaimDetail,
  ClaimListItem,
  ClaimStats,
  ClaimStatsQuery,
  ClaimStatsTop,
  ClaimStatsTopQuery,
  ClaimStatusCatalog,
  ClaimStatusChangeRequest,
  ClaimUpdateRequest,
  ClaimableOrder,
  ListResponse,
} from '@warehouse/shared';

import { api } from '../../../lib/api-client';
import { listQueryParams, listQueryKey, type ListState } from '../../../lib/list-query';

/**
 * The claims fetch layer. One file, so every page agrees on the query keys the
 * SSE `claims` topic invalidates (`useLiveQuery`).
 */

export const claimKeys = {
  all: ['claims'] as const,
  list: (state: ListState) => ['claims', 'list', listQueryKey(state)] as const,
  detail: (id: string) => ['claims', 'detail', id] as const,
  statuses: () => ['claims', 'statuses'] as const,
  stats: (query: Partial<ClaimStatsQuery>) => ['claims', 'stats', query] as const,
  statsTop: (query: Partial<ClaimStatsTopQuery>) => ['claims', 'stats', 'top', query] as const,
  order: (identifier: string) => ['claims', 'order', identifier] as const,
};

export function fetchClaims(state: ListState): Promise<ListResponse<ClaimListItem>> {
  return api.get<ListResponse<ClaimListItem>>('/claims', { query: listQueryParams(state) });
}

export function fetchClaim(id: string): Promise<ClaimDetail> {
  return api.get<ClaimDetail>(`/claims/${id}`);
}

export function createClaim(body: ClaimCreateRequest): Promise<ClaimDetail> {
  return api.post<ClaimDetail>('/claims', body);
}

export function updateClaim(id: string, body: ClaimUpdateRequest): Promise<ClaimDetail> {
  return api.patch<ClaimDetail>(`/claims/${id}`, body);
}

export function changeClaimStatus(id: string, body: ClaimStatusChangeRequest): Promise<ClaimDetail> {
  return api.post<ClaimDetail>(`/claims/${id}/status`, body);
}

export function deleteClaim(id: string): Promise<void> {
  return api.delete<void>(`/claims/${id}`);
}

export function fetchClaimStatuses(): Promise<{
  statuses: ClaimStatusCatalog;
  defaults: ClaimStatusCatalog;
}> {
  return api.get<{ statuses: ClaimStatusCatalog; defaults: ClaimStatusCatalog }>('/claims/statuses');
}

export function saveClaimStatuses(statuses: ClaimStatusCatalog): Promise<{ statuses: ClaimStatusCatalog }> {
  return api.put<{ statuses: ClaimStatusCatalog }>('/claims/statuses', { statuses });
}

export function fetchClaimStats(query: Partial<ClaimStatsQuery>): Promise<ClaimStats> {
  return api.get<ClaimStats>('/claims/stats', { query: toQuery(query) });
}

export function fetchClaimStatsTop(query: Partial<ClaimStatsTopQuery>): Promise<ClaimStatsTop> {
  return api.get<ClaimStatsTop>('/claims/stats/top', { query: toQuery(query) });
}

/** `identifier` is the order uuid or its printed number (`SO-100123`). */
export function fetchClaimableOrder(identifier: string): Promise<ClaimableOrder> {
  return api.get<ClaimableOrder>(`/claims/orders/${encodeURIComponent(identifier)}`);
}

export interface ClaimPhotoUpload {
  file: File;
  category: 'freight_note' | 'damaged_goods' | 'other';
}

/** Uploads a photo BEFORE the claim exists; returns the staged file id. */
export function stageClaimPhoto(upload: ClaimPhotoUpload): Promise<{ fileId: string; name: string }> {
  const form = new FormData();
  form.append('file', upload.file);
  form.append('category', upload.category);
  return api.post<{ fileId: string; name: string }>('/claims/photos', form);
}

export function uploadClaimPhoto(claimId: string, upload: ClaimPhotoUpload): Promise<ClaimDetail> {
  const form = new FormData();
  form.append('file', upload.file);
  form.append('category', upload.category);
  return api.post<ClaimDetail>(`/claims/${claimId}/photos`, form);
}

export function deleteClaimPhoto(claimId: string, fileId: string): Promise<ClaimDetail> {
  return api.delete<ClaimDetail>(`/claims/${claimId}/photos/${fileId}`);
}

function toQuery(query: Record<string, unknown>): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    out[key] = value as string | number;
  }
  return out;
}
